<template>
  <div :class="cardClasses" @click="handleClick">
    <!-- Card Header -->
    <div v-if="$slots.header || title || subtitle" class="card-header">
      <slot name="header">
        <div v-if="title || subtitle" class="card-header-content">
          <h3 v-if="title" class="card-title">{{ title }}</h3>
          <p v-if="subtitle" class="card-subtitle">{{ subtitle }}</p>
        </div>
      </slot>
      
      <!-- Header Actions -->
      <div v-if="$slots.actions" class="card-header-actions">
        <slot name="actions"></slot>
      </div>
    </div>
    
    <!-- Card Body -->
    <div :class="bodyClasses">
      <slot></slot>
    </div>
    
    <!-- Card Footer -->
    <div v-if="$slots.footer" class="card-footer">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  title?: string
  subtitle?: string
  variant?: 'default' | 'elevated' | 'flat' | 'stats' | 'feature'
  interactive?: boolean
  loading?: boolean
  compact?: boolean
  spacious?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  interactive: false,
  loading: false,
  compact: false,
  spacious: false
})

const emit = defineEmits<{
  click: [event: Event]
}>()

const cardClasses = computed(() => {
  const classes = ['card']
  
  // Variant classes
  if (props.variant !== 'default') {
    classes.push(`card--${props.variant}`)
  }
  
  // Interactive state
  if (props.interactive) {
    classes.push('card--interactive')
  }
  
  // Loading state
  if (props.loading) {
    classes.push('card--loading')
  }
  
  return classes
})

const bodyClasses = computed(() => {
  const classes = ['card-body']
  
  if (props.compact) {
    classes.push('card-body--compact')
  }
  
  if (props.spacious) {
    classes.push('card-body--spacious')
  }
  
  return classes
})

const handleClick = (event: Event) => {
  if (props.interactive) {
    emit('click', event)
  }
}
</script>

<style scoped>
.card-header-content {
  flex: 1;
}

.card-title {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--c-gray-800);
  line-height: var(--line-height-tight);
}

.card-subtitle {
  margin: var(--sp-1) 0 0 0;
  font-size: var(--font-size-sm);
  color: var(--c-gray-600);
  line-height: var(--line-height-normal);
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--sp-4);
}

.card-header-actions {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-shrink: 0;
}

.card--loading {
  position: relative;
  overflow: hidden;
}

.card--loading::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: shimmer 1.5s infinite;
  z-index: 1;
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}
</style>




